// External Libraries
import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
  Output,
  EventEmitter,
  Input,
  computed,
  signal,
  WritableSignal,
  inject
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  BehaviorSubject,
  Subject,
  Subscription,
  catchError,
  combineLatest,
  from,
  lastValueFrom,
  of,
  switchMap,
  tap
} from 'rxjs';
import { FronteggAuthService } from '@frontegg/angular';
import { ConfirmationService, PrimeTemplate } from 'primeng/api';
import { NgxSpinnerService } from 'ngx-spinner';

// Project Services
import {
  Area,
  ContentApiService,
  DocumentType
} from '../../services/content-api/content-api.service';
import { DocumentManagementService } from '../../services/document-management/document-management.service';
import { DocumentAnalysisService } from '../../services/document-analysis/document-analysis.service';
import { DocumentFilingService } from '../../services/document-filing/document-filing.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { TenantApiService } from '../../services/tenant-api/tenant-api.service';
import { SignOutService } from '../../services/sign-out-state/sign-out-state.service';
import { UserPrivilegesService } from '../../services/auth-privileges/auth-privileges.service';
import { BatchService } from '../../services/batch/batch.service';
import DOMPurify from 'dompurify';
import { ToastService } from '../../services/toast-messages/toast-messages.service';

// Project Models
import { FileSelectorAttachment } from '../../components/file-selector/file-selector.component';

// Utilities
import { isSupportedDocumentContent } from '../../utils/documentUtils';

// Environment Variables
import { AutoclassifierSdkService } from '../../services/autoclassifier-sdk/autoclassifier-sdk.service';
import { Document } from '../../models/document';
import {
  BatchDocumentRequest,
  BatchDocumentResponse,
  BatchResponse,
  BulkCreateBatchDocumentRequest
} from '../../models/sdk-interfaces';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService, DecodedToken } from '../../services/auth/auth.service';
import { jwtDecode } from 'jwt-decode';
import {
  BulkActionType,
  BulkDocumentActionsComponent
} from '../bulk-document-actions/bulk-document-actions.component';
import { LoadingOverlayComponent } from '../loading-overlay/loading-overlay.component';
import { NgStyle } from '@angular/common';
import { FileSelectorComponent } from '../file-selector/file-selector.component';
import { DocumentSplitComponent } from '../document-split/document-split.component';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { DocumentViewerComponent } from '../document-viewer/document-viewer.component';
import { KeyFieldsComponent } from '../key-fields/key-fields.component';
import { KeyFieldsComponent as KeyFieldsComponent_1 } from '../key-fields/key-fields.component';
import { Toast } from 'primeng/toast';
import { TranslatePipe } from '@ngx-translate/core';
import { MessageModule } from 'primeng/message';
import { ActivatedRoute } from '@angular/router';
import { FieldValue } from '../../models/etrieveContent';
import { UpdateBatchDocumentRequest } from '@softdocs/capture-ui-libraries/types/softdocs-capture-ui-libraries';

@Component({
  selector: 'app-capture',
  templateUrl: './capture.component.html',
  styleUrls: ['./capture.component.scss'],
  imports: [
    LoadingOverlayComponent,
    NgStyle,
    FileSelectorComponent,
    DocumentSplitComponent,
    Bind,
    Button,
    DocumentViewerComponent,
    KeyFieldsComponent_1,
    Toast,
    PrimeTemplate,
    TranslatePipe,
    MessageModule,
    BulkDocumentActionsComponent
  ]
})
export class CaptureComponent implements OnInit, OnDestroy {
  private fronteggAuthService = inject(FronteggAuthService);
  private featureFlagService = inject(FeatureFlagService);
  private tenantService = inject(TenantApiService);
  private contentService = inject(ContentApiService);
  private docMngmtService = inject(DocumentManagementService);
  private documentAnalysisService = inject(DocumentAnalysisService);
  private confirmationService = inject(ConfirmationService);
  private spinner = inject(NgxSpinnerService);
  private signOutService = inject(SignOutService);
  private autoclassifierSdkService = inject(AutoclassifierSdkService);
  private userPrivilegesService = inject(UserPrivilegesService);
  private batchService = inject(BatchService);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  sessionService = inject(DocumentFilingService);

  extractionCompleteEvent: Subject<any> = new Subject<any>();

  @Output() restartFilingEvent = new EventEmitter<void>();
  @Output() openEditBatchEvent = new EventEmitter<void>();
  @Output() deleteBatchEvent = new EventEmitter<void>();
  @Output() closeBatchEvent = new EventEmitter<void>();
  @Output() openAddDocumentsEvent = new EventEmitter<boolean>();
  @Output() openScanningDialogEvent = new EventEmitter<boolean>();

  @ViewChild(KeyFieldsComponent) keyFieldsComponent!: KeyFieldsComponent;

  @Input() parentType: 'adhoc' | 'batch' | 'outlook' | 'gmail' | 'printer' = 'adhoc';
  @Input() batchLoading: boolean = false;
  @Input() batch: WritableSignal<BatchResponse> = signal({} as BatchResponse);
  @Input() batchDocuments: WritableSignal<BatchDocumentResponse[]> = signal([]);
  @Input() enableBatchOptions: boolean = false;

  docExpirationUiEnabled = signal(false);

  // Computed values for template binding
  batchValue = computed(() => this.batch());
  batchDocumentsValue = computed(() => this.batchDocuments());
  batchCompletedSuccessfully = signal(false);
  reopenedBatch = signal(false);
  isBatchCompleted = computed(
    () => (this.batch().status ?? '').trim().toLowerCase() === 'completed'
  );

  private subscription = new Subscription();
  private signOutSubscription = new Subscription();
  private extractionStatusSubscription = new Subscription();

  autoclassificationFlag$ = new BehaviorSubject<boolean>(false);

  isAutoClassificationEnabled: boolean = false;
  isAutoclassificationFlagLoading: boolean = true;

  title = 'documentUpload';
  user?: any;

  tenantId: string = '';
  documentIds: string[] = [];
  areas: Area[] = [];

  showAddDocumentsModal = signal<boolean>(false);
  showBulkActions = signal<boolean>(false);
  showDocumentSplit = signal<boolean>(false);

  bulkActionType: BulkActionType = 'merge';

  isFiling: boolean = false;
  isLoadingFiles: boolean = false;
  currentMostConfidentDocument: any;
  contentUrl: string = '';

  /**
   * Gets the list of documents from the session service.
   */
  get documents(): Document[] {
    return this.sessionService.documents();
  }

  /**
   * Gets the index of the currently selected document from the session service.
   */
  get currentAttachmentIndex(): number {
    return this.sessionService.selectedIndex();
  }

  /**
   * True when the currently selected document has content the viewer cannot render.
   */
  get isCurrentFileUnsupported(): boolean {
    const content = this.documents[this.currentAttachmentIndex]?.fileContent;
    if (!content) return false;
    return !isSupportedDocumentContent(content);
  }

  /**
   * True when the currently selected document has expired.
   */
  isSelectedDocumentExpired = computed(
    () => this.sessionService.selectedDocument()?.isExpired ?? false
  );

  private canManageBatchDocuments = toSignal(
    this.userPrivilegesService.canPerformAction(
      'MANAGE_BATCH_DOCUMENTS',
      this.batch().department,
      this.batch().status
    ),
    { initialValue: false }
  );

  private canFileBatchDocuments = toSignal(
    this.userPrivilegesService.canPerformAction(
      'FILE_BATCH_DOCUMENT',
      this.batch().department,
      this.batch().status
    ),
    { initialValue: true }
  );

  showDeleteDocumentButton = computed(() => {
    if (
      this.parentType === 'outlook' ||
      this.parentType === 'gmail' ||
      this.batch().status === 'completed'
    ) {
      return false;
    }

    if (this.parentType === 'batch') {
      return this.canManageBatchDocuments();
    }
    return true;
  });

  disableFileByPrivilege = computed(() => {
    if (this.parentType !== 'batch') {
      return false;
    }

    return !this.canFileBatchDocuments();
  });

  /**
   * Initializes the component, setting up necessary data and feature flags
   */
  ngOnInit(): void {
    this.tenantId = localStorage.getItem('tenantId') || '';
    this.documentAnalysisService.setTenantId(this.tenantId);

    this.featureFlagService.getFlag('feature-capture-doc-expiration-ui', false).subscribe(flag => {
      this.docExpirationUiEnabled.set(flag);
    });

    if (this.tenantId) {
      this.subscription.add(
        this.fronteggAuthService.authState$.subscribe(authState => {
          if (authState.user?.accessToken) {
            this.user = authState.user;
          }
        })
      );
    }

    this.signOutSubscription = this.signOutService.signOut$.subscribe(signOut => {
      if (signOut) {
        this.canDeactivate();
      }
    });
  }

  /**
   * Starts the ad hoc filing process for the provided document IDs.
   * @param docIds - An array of document IDs to be processed.
   * @param token - Optional auth token to set for the authService.
   */
  startAdHocFiling(docIds: string[], token?: string) {
    if (token) {
      this.authService.setAuthToken(token);
      try {
        this.user = jwtDecode<DecodedToken>(token);
      } catch (error) {
        console.error('Error decoding JWT token:', error);
      }
    }
    this.documentIds = docIds;
    this.sessionService.setLoading(true);
    this.isLoadingFiles = true;
    this.isFiling = true;
    this.signOutService.isFilingContent = true;

    this.loadData();
  }

  /**
   * Retrieves the attachments for the file selector.
   * Maps from the session document list — no index math needed.
   */
  getFileSelectorAttachments(): FileSelectorAttachment[] {
    return this.sessionService.documents().map(doc => {
      let documentTypeCode = undefined;

      const extractionData = doc.extractionData;
      const hasUsableExtraction =
        extractionData?.record_status === 'extracted' ||
        extractionData?.record_status === 'extraction prompt failed';

      if (hasUsableExtraction) {
        documentTypeCode = extractionData.tenant_doctype || extractionData.ai_doctype;
      }

      return {
        name: doc.fileName,
        id: doc.fileId,
        completed: doc.completed,
        status: doc.status,
        classificationStarted: doc.classificationStarted,
        canceled: doc.canceled,
        fileContent: doc.fileContent,
        expirationDate: doc.expiresOn,
        isExpired: doc.isExpired,
        documentTypeCode: documentTypeCode
      } as FileSelectorAttachment;
    });
  }

  /**
   * Re-extracts the current document with a specific document type.
   * @param documentTypeName - The name of the document type to use for re-extraction.
   */
  async reExtractDocument(documentType: DocumentType): Promise<void> {
    const currentDoc = this.sessionService.selectedDocument();
    if (!currentDoc) return;

    const fileId = currentDoc.fileId;
    await this.resetDocumentExtractionState(fileId);

    const docToExtract = this.sessionService.getDocument(fileId);
    if (!docToExtract) return;

    this.startDocumentExtraction([docToExtract], true, documentType);
  }

  /**
   * Clears extraction/metadata caches and resets a document's session state ahead of a (re-)extraction call.
   * Shared by the single-document re-extraction flow and the bulk update flow.
   * @param fileId - The fileId of the document to reset.
   */
  private async resetDocumentExtractionState(fileId: string): Promise<void> {
    this.documentAnalysisService.cancelExtraction([fileId]);
    this.documentAnalysisService.clearDocumentState(fileId);

    try {
      await this.docMngmtService.clearDocumentContentMetadata(this.tenantId, fileId);
    } catch (err) {
      console.error('Failed to clear metadata before re-extraction:', err);
    }

    this.sessionService.patchDocument(fileId, {
      status: undefined,
      extractionData: undefined,
      extractionStatus: 'pending',
      extractionCancelled: false,
      classificationStarted: true
    });
  }

  /**
   * Re-extracts a batch of documents with a specific document type, preserving each document's
   * selected area/document type so they still show (with empty key fields) if extraction fails.
   * @param documents - The documents to re-extract.
   * @param documentType - The document type code selected
   */
  private async reExtractDocuments(
    documents: Document[],
    documentType: DocumentType
  ): Promise<void> {
    await Promise.all(
      documents.map(document => this.resetDocumentExtractionState(document.fileId))
    );

    const docsToExtract = documents
      .map(document => this.sessionService.getDocument(document.fileId))
      .filter((document): document is Document => !!document);

    if (docsToExtract.length === 0) return;

    this.startDocumentExtraction(docsToExtract, true, documentType);
  }

  /**
   * Starts document extraction using the DocumentAnalysisService.
   * @param documents - An array of Document objects to be extracted.
   * @param force - A boolean indicating whether to force extraction, bypassing any caches.
   * @param selectedDocumentType - Optional document type name to use for extraction, which may influence the extraction logic.
   */
  private startDocumentExtraction(
    documents: Document[],
    force: boolean = false,
    selectedDocumentType?: DocumentType
  ): void {
    this.documentAnalysisService
      .startExtraction(documents, force, selectedDocumentType?.name)
      .catch(() => {
        this.toastService.error('idpErrors.error', 'idpErrors.documentExtractionFailed');
      });

    const terminalStatuses = new Set([
      'extracted',
      'failed',
      'extraction failed',
      'extraction prompt failed'
    ]);
    const trackedIds = new Set(documents.map(d => d.fileId));
    const processedTerminalStatuses = new Set<string>();

    this.extractionStatusSubscription.add(
      this.documentAnalysisService.getAllExtractionStatuses().subscribe(statusMap => {
        statusMap.forEach((state, documentId) => {
          if (!trackedIds.has(documentId)) return;

          const doc = this.sessionService.getDocument(documentId);
          if (!doc || doc.extractionCancelled) return;

          if (state.extractionData) {
            if (
              ((state.extractionData.tenant_doctype == null &&
                state.extractionData.ai_doctype == null) ||
                state.extractionStatus == 'failed') &&
              typeof selectedDocumentType != undefined
            ) {
              state.extractionData.tenant_doctype = selectedDocumentType?.code;
            }
          } else if (selectedDocumentType) {
            state.extractionData = { tenant_doctype: selectedDocumentType?.code };
          }

          this.sessionService.patchDocument(documentId, {
            extractionStatus: state.extractionStatus,
            extractionRecordId: state.extractionRecordId,
            ...(state.extractionData
              ? {
                  extractionData: {
                    ...state.extractionData
                  }
                }
              : {}),
            ...(state.status ? { status: state.status } : {})
          });

          const isTerminal = terminalStatuses.has(state.extractionStatus);

          if (isTerminal) {
            if (processedTerminalStatuses.has(documentId)) {
              return;
            }
            processedTerminalStatuses.add(documentId);

            const isSelected = this.sessionService.session().selectedDocumentId === documentId;
            if (isSelected) {
              this.extractionCompleteEvent.next(this.sessionService.selectedDocument());
            }
          }
        });
      })
    );
  }

  /**
   * Initializes data to be used by the component
   */
  loadData(): void {
    this.featureFlagService.identify({
      userId: this.user.customClaims.principal_id,
      tenantId: this.tenantId
    });

    this.sessionService.reset();

    this.subscription.add(
      this.featureFlagService
        .getFlag('license-IDP', false)
        .pipe(
          switchMap(flagValue => {
            this.isAutoclassificationFlagLoading = false;
            this.isAutoClassificationEnabled = flagValue as boolean;
            this.autoclassificationFlag$.next(flagValue);
            return this.tenantService.getTenantUrls(this.tenantId);
          }),
          switchMap(tenantUrls => {
            this.contentService.setBaseUrl(tenantUrls.contentApiUrl);
            this.contentUrl = tenantUrls.contentUrl;
            return this.contentService.getAreas();
          }),
          switchMap(areas => {
            this.areas = areas;
            this.contentService.getDocumentTypes().subscribe();

            const loadOptions = {
              loadContent: true,
              loadExtractionData: this.isAutoClassificationEnabled,
              sequential: this.parentType === 'batch'
            };

            return from(
              this.docMngmtService.loadDocuments(this.tenantId, this.documentIds, loadOptions)
            );
          }),
          catchError(error => {
            console.error('Error in chained requests', this.sanitizeErrorMessage(error));
            this.toastService.error('error', 'dataError', 10000);
            this.isLoadingFiles = false;
            return of([]);
          })
        )
        .subscribe(documents => {
          if (this.parentType === 'batch') {
            documents.forEach(doc => {
              const batchDoc = this.batchDocuments().find(bd => bd.documentId === doc.fileId);
              if (batchDoc?.status === 'filed') {
                doc.completed = true;
              }
              doc.expiresOn = batchDoc?.expiresOn;
              doc.isExpired = batchDoc?.isExpired;
            });
          }

          this.sessionService.setDocuments(documents);
          this.sessionService.setLoading(false);
          this.isLoadingFiles = false;

          if (documents.length > 0) {
            const firstSelectable = this.sessionService.getFirstSelectableDocument();
            if (firstSelectable) {
              this.selectDocument(firstSelectable.fileId);
            }

            if (this.isAutoClassificationEnabled) {
              const terminalStatuses = new Set([
                'extracted',
                'failed',
                'extraction failed',
                'extraction prompt failed'
              ]);
              const docsNeedingExtraction = documents.filter(
                doc => !doc.extractionStatus || !terminalStatuses.has(doc.extractionStatus)
              );
              if (docsNeedingExtraction.length > 0) {
                this.startDocumentExtraction(docsNeedingExtraction);
              }
            }
          }
        })
    );
  }

  /**
   * Selects a document by fileId and triggers a form build.
   * @param fileId - The fileId of the document to select.
   */
  selectDocument(fileId: string): void {
    const outgoingId = this.sessionService.session().selectedDocumentId;

    // prevents clicking the active file-selector item from reverting unsaved changes.
    if (outgoingId === fileId) return;

    this.sessionService.selectDocument(fileId);
  }

  /**
   * Ends the status check and cleans up
   */
  endStatusCheckInterval(): void {
    // Cleanup handled by DocumentAnalysisService
    this.documentAnalysisService.reset();
  }

  /**
   * Uploads the current document Etrieve Content.
   */
  exportDocument($event: any): void {
    const { formData, areaCode, docTypeCode } = $event;

    const selectedDoc = this.sessionService.selectedDocument();
    if (!selectedDoc) return;

    const fileId = selectedDoc.fileId;

    const attachment = {
      content: { content: selectedDoc.fileContent, format: 'base64' },
      attachmentType: selectedDoc.contentType,
      requestId: selectedDoc.requestId,
      name: selectedDoc.fileName,
      contentType: selectedDoc.contentType,
      contentId: fileId,
      id: '',
      isInline: false,
      size: 0
    };

    this.contentService
      .uploadDocument(attachment, areaCode, docTypeCode, formData)
      .pipe(
        switchMap(response => {
          const ecmDocId = response.metadataResponse.id;
          const authToken = this.authService.getAuthToken()!;
          return from(
            this.autoclassifierSdkService.documentManagementSdk.updateDocument(
              this.tenantId,
              fileId,
              { EcmDocId: ecmDocId },
              authToken
            )
          ).pipe(
            tap(() => this.sessionService.patchDocument(fileId, { ecmDocId })),
            catchError(error => {
              console.error('Error updating document:', error);
              throw error;
            })
          );
        })
      )
      .subscribe({
        error: error => {
          console.error('Failed to upload document', this.sanitizeErrorMessage(error));
          this.toastService.error('error', 'uploadError', 10000);
          this.keyFieldsComponent.onExportComplete();
        },
        complete: () => {
          this.sessionService.patchDocument(fileId, { completed: true });
          this.toastService.success('uploadToastTitleWebCapture', 'uploadToastMessageWebCapture');
          this.keyFieldsComponent.onExportComplete();

          if (this.parentType === 'batch') {
            this.handleBatchDocumentFiled(fileId);
          } else {
            this.handleAdhocDocumentFiled(fileId);
          }
        }
      });
  }

  /**
   * Updates batch state after a document is successfully filed.
   * @param fileId - The ID of the document that was filed, used to update the corresponding batch document record.
   */
  private handleBatchDocumentFiled(fileId: string): void {
    const currentDocument = this.batchDocuments().find(doc => doc.documentId === fileId);
    const updateRequest: UpdateBatchDocumentRequest = {
      version: currentDocument?.version || 1,
      source: currentDocument?.source,
      status: 'filed'
    };

    this.batchService
      .updateBatchDocument(this.batch().batchId!, fileId, updateRequest)
      .then(async response => {
        if (response.batch) this.batch.set(response.batch);
        const updatedDocs = this.batchDocuments().map(doc =>
          doc.documentId === fileId ? { ...doc, status: 'filed' as const } : doc
        );
        this.batchDocuments.set(updatedDocs);

        const next = this.sessionService.getFirstSelectableDocument();
        if (next && !next.completed && !next.canceled) {
          this.selectDocument(next.fileId);
        } else {
          await this.checkAndCompleteBatch();
        }
      });
  }

  /**
   * Deletes the document from the backend after it is successfully filed (adhoc).
   * @param fileId - The ID of the document that was filed, used to identify which document to delete from the backend and session.
   */
  private handleAdhocDocumentFiled(fileId: string): void {
    const authToken = this.authService.getAuthToken();
    this.autoclassifierSdkService.documentManagementSdk
      .deleteDocument(this.tenantId, fileId, authToken)
      .then(() => {
        const next = this.sessionService.getFirstSelectableDocument();
        if (next && !next.completed && !next.canceled) {
          this.selectDocument(next.fileId);
        } else {
          return;
        }
      });
  }

  /**
   * Cancels the file filing process for the current attachment
   */
  cancelFileFiling(): void {
    this.confirmationService.confirm({
      message: '',
      key: 'deleteAdHocFileConfirmDialog',
      accept: () => {
        if (this.parentType === 'batch') {
          this.deleteBatchDocument();
        } else {
          this.deleteDocument();
        }
      }
    });
  }

  /**
   * Resets the component back to its initial unfiled state.
   */
  restartAdHocFiling(): void {
    this.autoclassificationFlag$.next(false);
    this.sessionService.reset();
    this.documentIds = [];
    this.isFiling = false;
    this.batchCompletedSuccessfully.set(false);
    this.restartFilingEvent.emit();
  }

  /**
   * Deletes the current document from the backend and removes it from the session.
   */
  deleteDocument(): Promise<void> {
    const currentDoc = this.sessionService.selectedDocument();
    if (!currentDoc) return Promise.resolve();

    const fileId = currentDoc.fileId;
    const authToken = this.authService.getAuthToken();

    return this.autoclassifierSdkService.documentManagementSdk
      .deleteDocument(this.tenantId, fileId, authToken)
      .then(() => {
        this.documentAnalysisService.cancelExtraction([fileId]);
        this.sessionService.removeDocument(fileId);
      });
  }

  /**
   * Removes the batch document record then deletes the underlying document.
   */
  deleteBatchDocument(): void {
    const currentDoc = this.sessionService.selectedDocument();
    if (!currentDoc) return;

    const fileId = currentDoc.fileId;

    this.batchService
      .removeBatchDocument(this.batch().batchId!, fileId)
      .then(async updatedBatch => {
        const updatedDocs = this.batchDocuments().filter(doc => doc.documentId !== fileId);
        this.batchDocuments.set(updatedDocs);
        this.batch.set(updatedBatch);
        await this.deleteDocument();
        await this.checkAndCompleteBatch();
      });
  }

  /**
   * Checks if all documents in the batch have been filed or deleted and then completes the batch
   */
  private async checkAndCompleteBatch(): Promise<void> {
    if (this.parentType !== 'batch' || this.batch().status === 'completed') {
      return;
    }

    const docs = this.sessionService.documents();
    const allDone = docs.length === 0 || docs.every(d => d.completed || d.canceled);

    if (allDone) {
      try {
        const response = await this.batchService.updateBatch(this.batch().batchId!, {
          status: 'completed',
          version: this.batch().version
        });

        this.batch.set(response);
        this.reopenedBatch.set(false);
        this.batchCompletedSuccessfully.set(true);
      } catch (error) {
        this.toastService.error('error', 'idpErrors.batchCompletionError', 10000);
      }
    }
  }

  /**
   * Cancels the auto-classification process for a document.
   * @param index - The display index of the document to cancel auto-classification for.
   */
  cancelFileAutoClassification(documentType?: DocumentType): void {
    const doc = this.sessionService.documents()[this.currentAttachmentIndex];
    if (!doc) return;

    this.sessionService.patchDocument(doc.fileId, {
      extractionCancelled: true,
      extractionStatus: 'failed',
      status: { documents: [] },
      extractionData: { tenant_doctype: documentType?.code }
    });

    this.documentAnalysisService.cancelExtraction([doc.fileId]);
    this.extractionCompleteEvent.next(this.sessionService.selectedDocument());
  }

  /**
   * Cleans up component resources and unsubscribes from observables
   */
  ngOnDestroy(): void {
    this.endStatusCheckInterval();
    this.subscription.unsubscribe();
    this.signOutSubscription.unsubscribe();
    this.extractionStatusSubscription.unsubscribe();
  }

  /**
   * Opens the merge documents dialog
   */
  openMergeDocuments() {
    this.bulkActionType = 'merge';
    this.showBulkActions.set(true);
  }

  /**
   * Opens the update documents dialog
   */
  openUpdateDocuments() {
    this.bulkActionType = 'update';
    this.showBulkActions.set(true);
  }

  /**
   * Applies a selected Area/Document Type to a batch of documents from the bulk update workflow.
   * @param event - The documents to update along with the selected area and document type.
   * @param event.documents - The documents to apply the selected area and document type to.
   * @param event.area - The area code selected for the batch update.
   * @param event.documentTypeCode - The document type code selected for the batch update, if any.
   * @param event.documentTypeName - The document type name selected for the batch update, used to drive re-extraction.
   */
  async handleBulkUpdatedocumentDetails(event: {
    documents: FileSelectorAttachment[];
    area: string;
    documentTypeCode?: string;
    documentTypeName?: string;
  }): Promise<void> {
    const { documents: attachments, area, documentTypeCode, documentTypeName } = event;

    attachments.forEach(attachment => this.sessionService.markAsManuallyChanged(attachment.id));

    if (!documentTypeCode) {
      // Area only: reset each document's document type, same as manually clearing it. No re-extraction.
      await Promise.all(
        attachments.map(async attachment => {
          try {
            const formData = {
              area,
              documentTypeCode: ''
            };

            await this.docMngmtService.saveKeyFieldsMetadata(
              this.tenantId,
              attachment.id,
              formData
            );
          } catch (error) {
            console.error('[CaptureComponent] Failed to bulk update area:', error);
          }
        })
      );

      this.closeBulkDocumentActions();
      return;
    }

    const documents = attachments
      .map(attachment => this.sessionService.getDocument(attachment.id))
      .filter((document): document is Document => !!document);

    if (this.isAutoClassificationEnabled) {
      await this.reExtractDocuments(documents, {
        code: documentTypeCode,
        name: documentTypeName
      } as DocumentType);
    } else {
      await Promise.all(
        documents.map(async document => {
          try {
            const formData = {
              area,
              documentTypeCode
            };
            await this.docMngmtService.saveKeyFieldsMetadata(
              this.tenantId,
              document.fileId,
              formData
            );
          } catch (error) {
            console.error('[CaptureComponent] Failed to bulk update key fields:', error);
          }
        })
      );
    }

    this.closeBulkDocumentActions();
  }

  /**
   * Closes the bulk document actions dialog (merge or update) and refreshes the current document.
   */
  closeBulkDocumentActions() {
    this.showBulkActions.set(false);
  }

  /**
   * Opens the split document feature
   */
  openDocumentSplit() {
    this.showDocumentSplit.set(true);
  }

  /**
   * Closes the split documents feature and refreshes the current document.
   */
  closeDocumentSplit() {
    this.showDocumentSplit.set(false);
    const selected = this.sessionService.selectedDocument();
    if (selected) {
      this.selectDocument(selected.fileId);
    }
  }

  /**
   * Checks if the component can be safely deactivated
   * @returns Promise that resolves to true if deactivation can proceed, false otherwise
   */
  canDeactivate(): Promise<boolean> {
    if (!this.isFiling) {
      return Promise.resolve(true);
    } else {
      return new Promise(resolve => {
        this.confirmationService.confirm({
          message: 'closeDocumentsConfirmation',
          key: 'tabChangeDialog',
          accept: async () => {
            if (this.parentType === 'batch') {
              await this.checkAndCompleteBatch();
            }
            if (this.signOutService.getSignOutState()) {
              this.signOutService.signOut();
            }
            resolve(true);
          },
          reject: () => {
            if (this.signOutService.getSignOutState()) {
              this.signOutService.setSignOutState(false);
            }
            resolve(false);
          }
        });
      });
    }
  }

  /**
   * Puts newly split documents into the session and updates the original document's content.
   * @param response - The response object containing the results of the document split operation, including new document IDs and the original document ID.
   */
  handleDocumentSplit(response: any) {
    if (!response.splitDocumentResults?.splitDocumentResult) return;

    const results = response.splitDocumentResults.splitDocumentResult;
    const newDocIds: string[] = results
      .filter((r: any) => r.type === 'new')
      .map((r: any) => r.documentId);

    const originalDocId: string | undefined = results.find(
      (r: any) => r.type === 'original'
    )?.documentId;

    const loadOptions = {
      loadContent: true,
      loadExtractionData: this.isAutoClassificationEnabled
    };

    const newDocs$ =
      newDocIds.length > 0
        ? from(this.docMngmtService.loadDocuments(this.tenantId, newDocIds, loadOptions))
        : of([] as Document[]);

    const originalRefresh$ = originalDocId
      ? from(this.docMngmtService.refreshDocumentContent(this.tenantId, originalDocId))
      : of(null);

    if (this.parentType === 'batch') {
      this.batchService
        .getBatch(this.batch().batchId!)
        .then(refreshedBatch => this.batch.set(refreshedBatch));
    }

    combineLatest([newDocs$, originalRefresh$]).subscribe({
      next: ([newDocs, updatedContent]) => {
        newDocs.forEach(doc => this.sessionService.addDocument(doc));

        const docsToClassify: Document[] = [...newDocs];

        if (originalDocId && updatedContent !== null) {
          this.sessionService.patchDocument(originalDocId, {
            fileContent: updatedContent,
            extractionStatus: undefined,
            extractionRecordId: undefined,
            extractionData: undefined,
            extractionCancelled: false,
            status: undefined
          });

          this.documentAnalysisService.clearExtractionState([originalDocId]);

          const authToken = this.authService.getAuthToken()!;
          const originalDoc = this.sessionService.getDocument(originalDocId);
          const oldRecordId = originalDoc?.extractionRecordId;

          const deleteOldRecord$ = oldRecordId
            ? from(
                this.autoclassifierSdkService.transcriptMetaDataSdk.deleteDocument(
                  authToken,
                  oldRecordId
                )
              ).pipe(
                catchError(err => {
                  console.error('Error deleting old extraction record:', err);
                  return of(null);
                })
              )
            : of(null);

          deleteOldRecord$.subscribe(() => {
            if (originalDoc) docsToClassify.push({ ...originalDoc });
            if (this.isAutoClassificationEnabled && docsToClassify.length > 0) {
              this.startDocumentExtraction(docsToClassify, true);
            }
          });
          return;
        }

        if (this.isAutoClassificationEnabled && docsToClassify.length > 0) {
          this.startDocumentExtraction(docsToClassify, true);
        }
      },
      error: (error: any) => {
        console.error('Error handling document split:', error);
        this.toastService.error('idpErrors.error', 'idpErrors.errorProcessingSplitDocuments');
      }
    });
  }

  /**
   * Handles the successful merge event from the merge-documents component.
   * @param event - The event object containing the merged document and the IDs of the documents that were merged and should be removed.
   * @param event.mergedDocument - The Document object representing the newly merged document that should be added to the session and optionally to the batch.
   * @param event.idsToRemove - An array of document IDs that were merged into the new document and should be removed from the session and optionally from the batch.
   */
  async handleSuccessfulMerge(event: { mergedDocument: Document; idsToRemove: string[] }) {
    const { mergedDocument, idsToRemove } = event;

    if (!mergedDocument) return;

    const loadOptions = {
      loadContent: true,
      loadExtractionData: this.isAutoClassificationEnabled
    };

    try {
      const [loadedDoc] = await Promise.all([
        this.docMngmtService.loadDocument(this.tenantId, mergedDocument.fileId, loadOptions),
        this.removeMergedDocuments(idsToRemove)
      ]);

      idsToRemove?.forEach(id => this.sessionService.removeDocument(id));

      if (this.parentType === 'batch') {
        await this.reopenAndAddDocuments([loadedDoc.fileId]);
      } else {
        this.sessionService.addDocument(loadedDoc);

        if (this.isAutoClassificationEnabled) {
          this.startDocumentExtraction([loadedDoc]);
        }
      }

      this.showBulkActions.set(false);
      this.selectDocument(loadedDoc.fileId);
    } catch (error) {
      console.error('Error handling merge:', error);
      this.toastService.error('error', 'errorLoadingMergedDocument', 5000);
    }
  }

  /**
   * Removes the given document IDs from the batch
   * @param idsToRemove - The document IDs to remove from the batch.
   */
  private async removeMergedDocuments(idsToRemove: string[]): Promise<void> {
    if (this.parentType !== 'batch' || !idsToRemove?.length) return;

    for (const id of idsToRemove) {
      const updatedBatch = await this.batchService.removeBatchDocument(this.batch().batchId!, id);
      this.batch.set(updatedBatch);
    }
  }

  /**
   * Handles errors during document upload
   * @param {string} error - The error message to display
   */
  handleDocumentUploadError(error: string) {
    this.toastService.error('error', 'idpErrors.documentUploadFailed', 10000);
  }
  /**
   * Cleans up the error message by removing unwanted characters
   * @param error The error object or message to sanitize.
   * @returns The sanitized error message as a string.
   */
  sanitizeErrorMessage(error: any): string {
    return JSON.stringify(error).replace(/[\r\n\t]/g, ' ');
  }

  /**
   * Emits event to capture component to open add documents modal
   */
  openAddDocumentsModal(): void {
    this.openAddDocumentsEvent.emit(true);
  }

  /**
   * Emits event to capture component to open scanning dialog
   */
  openScanningDialog(): void {
    this.openScanningDialogEvent.emit(true);
  }

  /**
   * Emits event to capture component to open edit batch modal
   */
  openEditBatchModal() {
    this.openEditBatchEvent.emit();
  }

  /**
   * Emits event to capture component to show batch deletion confirmation
   */
  confirmDeleteBatch() {
    this.deleteBatchEvent.emit();
  }

  /**
   * Emits event to capture component to show batch closure confirmation
   */
  confirmCloseBatch() {
    this.closeBatchEvent.emit();
  }

  /**
   * If the batch is completed, checks it out first to reopen it then send documents to be added to the batch.
   * @param documentIds - An array of document IDs to be added to the current batch.
   */
  reopenAndAddDocuments(documentIds: string[]): Promise<void> {
    const checkoutIfNeeded =
      this.batch().status === 'completed'
        ? this.batchService.checkoutBatch(this.batch().batchId!).then(response => {
            this.batch.set(response);
            this.reopenedBatch.set(true);
            this.batchCompletedSuccessfully.set(false);
          })
        : Promise.resolve();

    return checkoutIfNeeded.then(() => this.addDocumentsToBatch(documentIds));
  }

  /**
   * Performs the actual bulk document creation against the batch API and loads the new documents into session.
   * @param documentIds - An array of document IDs to be added to the current batch.
   */
  private addDocumentsToBatch(documentIds: string[]): Promise<void> {
    const currentBatchDocumentCount = this.batchDocuments().length;
    const bulkCreateRequest: BulkCreateBatchDocumentRequest = {
      documents: documentIds.map(
        (fileId, index) =>
          ({
            documentId: fileId,
            source: 'document',
            documentOrder: currentBatchDocumentCount + index + 1
          }) as BatchDocumentRequest
      )
    };

    return this.batchService
      .bulkCreateBatchDocuments(this.batch().batchId!, bulkCreateRequest)
      .then(response => {
        if (response.batch) this.batch.set(response.batch);
        this.openAddDocumentsEvent.emit(false);

        const loadOptions = {
          loadContent: true,
          loadExtractionData: this.isAutoClassificationEnabled,
          sequential: true
        };

        return lastValueFrom(
          from(this.docMngmtService.loadDocuments(this.tenantId, documentIds, loadOptions))
        );
      })
      .then(loadedDocs => {
        loadedDocs.forEach(doc => this.sessionService.addDocument(doc));

        if (this.isAutoClassificationEnabled) {
          this.startDocumentExtraction(loadedDocs);
        }
        if (loadedDocs.length > 0) {
          this.selectDocument(loadedDocs[0].fileId);
        }
      })
      .catch(error => {
        console.error('Error adding documents to batch:', error);
        throw error;
      });
  }

  /**
   * Handles scanned files by uploading them and adding to batch
   * @param files Array of scanned File objects to be processed
   */
  addScannedDocumentsToBatch(files: File[]): void {
    const fileArray = Array.from(files);
    const documentIds: string[] = [];

    const authToken = this.authService.getAuthToken()!;
    Promise.all(
      fileArray.map(file =>
        this.autoclassifierSdkService.documentManagementSdk.uploadDocument(
          this.tenantId,
          file,
          undefined,
          undefined,
          authToken
        )
      )
    )
      .then(responses => {
        responses.forEach(response => documentIds.push(response.id));
        return this.reopenAndAddDocuments(documentIds);
      })
      .catch(error => {
        console.error('Error uploading scanned files:', error);
        this.toastService.error('error', 'idpErrors.scanUploadFailed', 10000);
      });
  }
}
